<template>
  <div class="organization-concepts-tab">
    <v-row>
      <v-col cols="12">
        <v-card
          :loading="conceptsLoading || orgaConceptsLoading"
          :disabled="conceptsLoading || orgaConceptsLoading"
        >
          <v-card-title>Overview</v-card-title>
          <v-card-text>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate>
                  <span>Not Imported Concepts ({{notImportedConcepts.length}}/{{concepts.length}})</span>
                  <template v-slot:actions>
                    <v-icon
                      :color="notImportedConcepts.length === 0 ? 'success':'error'"
                    >{{notImportedConcepts.length === 0 ? 'mdi-check':'mdi-alert-circle'}}</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    class="title-1"
                  >The following concepts are in Kitchen Manager system but not referenced in IAM:</div>
                  <v-list dense shaped max-height="300px" style="overflow-y: auto;" two-line>
                    <v-list-item-group>
                      <v-list-item v-for="con in notImportedConcepts" :key="con.uuid" dense>
                        <v-list-item-avatar tile>
                          <v-img :src="con.logo" contain></v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>UUID : {{con.uuid}}</v-list-item-title>
                          <v-list-item-subtitle>Label: {{con.label}}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn color="primary" small rounded @click="importConcept(con.uuid)">
                            <v-icon left small>publish</v-icon>
                            <span>Import</span>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
              <v-expansion-panel>
                <v-expansion-panel-header disable-icon-rotate>
                  <span>Unknown Concepts ({{unknownConcepts.length}})</span>
                  <template v-slot:actions>
                    <v-icon
                      :color="unknownConcepts.length ? 'error': 'success'"
                    >{{unknownConcepts.length ? 'mdi-alert-circle': 'mdi-check'}}</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div
                    v-if="unknownConcepts.length"
                    class="title-1"
                  >The following concepts are in IAM system but not referenced in Kitchen Manager:</div>
                  <div v-else class="title-1">No unknown concept.</div>
                  <v-list dense shaped max-height="300px" style="overflow-y: auto;">
                    <v-list-item-group>
                      <v-list-item v-for="con in unknownConcepts" :key="con.uuid" dense>
                        <v-list-item-content>
                          <v-list-item-title>UUID : {{con.uuid}}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-btn color="error" small rounded @click="removeConcept(con.uuid)">
                            <v-icon left small>delete</v-icon>
                            <span>Remove</span>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12">
        <v-card
          :loading="conceptsLoading || orgaConceptsLoading"
          :disabled="conceptsLoading || orgaConceptsLoading"
        >
          <v-card-title>Available Concepts</v-card-title>
          <v-row class="ma-1">
            <v-col xs="12" sm="6" md="4" v-for="concept in concepts" :key="concept.uuid">
              <v-card>
                <div class="d-flex flex-no-wrap justify-space-between">
                  <div>
                    <v-card-title>{{concept.label}}</v-card-title>
                    <v-card-subtitle>{{concept.uuid}}</v-card-subtitle>
                  </div>
                  <v-avatar class="ma-3" size="125" tile>
                    <v-img :src="concept.logo" contain></v-img>
                  </v-avatar>
                </div>
                <v-card-actions>
                  <v-btn
                    :disabled="organizationConceptsUuids.includes(concept.uuid)"
                    v-if="!organizationConceptsUuids.includes(concept.uuid)"
                    color="primary"
                    dark
                    small
                    rounded
                    @click="importConcept(concept.uuid)"
                  >
                    <v-icon left small>publish</v-icon>
                    <span>Import</span>
                  </v-btn>

                  <v-btn
                    class="ml-2"
                    color="error"
                    v-if="organizationConceptsUuids.includes(concept.uuid)"
                    :disabled="!organizationConceptsUuids.includes(concept.uuid)"
                    small
                    rounded
                    @click="removeConcept(concept.uuid)"
                  >
                    <v-icon left small>delete</v-icon>
                    <span>Remove</span>
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-icon
                    class="ml-2"
                    :color="organizationConceptsUuids.includes(concept.uuid) ? 'success':'error'"
                  >{{organizationConceptsUuids.includes(concept.uuid) ? 'turned_in':'turned_in_not'}}</v-icon>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  props: {
    organizationConcepts: { type: Array, required: true },
    concepts: { type: Array, required: true },
    conceptsLoading: { type: Boolean, required: true },
    orgaConceptsLoading: { type: Boolean, required: true }
  },
  computed: {
    importedConcepts() {
      return this.concepts.filter(concept =>
        this.organizationConceptsUuids.includes(concept.uuid)
      );
    },
    notImportedConcepts() {
      return this.concepts.filter(
        concept => !this.organizationConceptsUuids.includes(concept.uuid)
      );
    },
    unknownConcepts() {
      return this.organizationConcepts.filter(
        concept => !this.concepts.map(con => con.uuid).includes(concept.uuid)
      );
    },
    organizationConceptsUuids() {
      return this.organizationConcepts.map(concept => concept.uuid);
    }
  },
  methods: {
    importConcept(conceptUuid) {
      this.$emit("importConcept", conceptUuid);
    },
    removeConcept(conceptUuid) {
      this.$emit("removeConcept", conceptUuid);
    }
  }
};
</script>

<style>
</style>